// src/components/Menu.tsx
import React, { useState, useEffect } from 'react';
import { List, ListItem, ListItemButton, ListItemText, Divider, Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import app from '../../services/firebase';
import LegalMenu from './LegalMenu';
import AdminMenu from './AdminMenu';
import AuthorMenu from './AuthorMenu';
import { APP_VERSION, BUILD_NUMBER, APP_COPYRIGHT_YEARS } from '../../config.js';

const auth = getAuth(app);

interface MenuItem {
  text: string;
  to: string;
}

interface MenuProps {
  toggleMenu?: () => void;
}

const menuItems: MenuItem[] = [
  { text: 'About', to: '/about' },
  { text: 'Contact Us', to: '/contactform' },
  { text: 'List Your Projects', to: '/contactregistrylist' },
  { text: 'Resources', to: '/resources' },
  { text: 'Support', to: '/support' },
  { text: 'Blog', to: 'https://mobipointofview.blogspot.com' },
];

const Menu: React.FC<MenuProps> = ({ toggleMenu }) => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isAuthorClaim, setIsAuthorClaim] = useState<boolean>(false);
  const [isLegalOpen, setIsLegalOpen] = useState<boolean>(false);
  const [isAdminOpen, setIsAdminOpen] = useState<boolean>(false);
  const [isAuthorMenuOpen, setAuthorMenuOpen] = useState<boolean>(false);

  const handleLegalClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsLegalOpen((prev) => !prev);
  };

  const handleAdminClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsAdminOpen((prev) => !prev);
  };

  const handleAuthorMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAuthorMenuOpen((prev) => !prev);
  };

  const handleSubmenuItemClick = () => {
    if (toggleMenu) {
      toggleMenu();
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult(true);
          console.log('ID Token Result:', idTokenResult);
          setIsAdmin(!!idTokenResult.claims.admin);
          setIsAuthorClaim(!!idTokenResult.claims.author);
          const isAdminClaim = !!idTokenResult.claims.admin;
          const isAuthorClaim = !!idTokenResult.claims.author;
          console.log(`Admin claim found: ${isAdminClaim}`);
          console.log(`Author claim found: ${isAuthorClaim}`);
        } catch (error) {
          console.error('Error fetching user claims:', error);
          setIsAdmin(false);
          setIsAuthorClaim(false);
        }
      } else {
        setIsAdmin(false);
        setIsAuthorClaim(false);
      }
    });
    return () => unsubscribe();
  }, []);

  return (
    <Box
      sx={{
        width: { xs: '75vw', sm: '320px' },
        maxWidth: 400,
        minWidth: 250,
        height: '100vh',
        bgcolor: '#FFF8EE', // Matches site branding background
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1200,
      }}
    >
      {/* 1. SCROLLABLE NAV LINKS SECTION */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
        <List component="nav">
          {/* Top Menu Items */}
          {menuItems.slice(0, 3).map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.to}
                onClick={handleSubmenuItemClick}
              >
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}

          {/* Legal Submenu */}
          <ListItem disablePadding sx={{ display: 'block' }}>
            <LegalMenu
              isLegalOpen={isLegalOpen}
              onLegalClick={handleLegalClick}
              toggleMenu={handleSubmenuItemClick}
            />
          </ListItem>

          {/* Remaining Menu Items */}
          {menuItems.slice(3).map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.to}
                onClick={handleSubmenuItemClick}
              >
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}

          <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.12)' }} />

          {/* Admin Menu */}
          {isAdmin && (
            <ListItem disablePadding sx={{ display: 'block' }}>
              <AdminMenu
                isAdminOpen={isAdminOpen}
                onAdminClick={handleAdminClick}
                toggleMenu={handleSubmenuItemClick}
              />
            </ListItem>
          )}

          {/* Author Menu */}
          {isAuthorClaim && (
            <ListItem disablePadding sx={{ display: 'block' }}>
              <AuthorMenu
                isAuthorMenuOpen={isAuthorMenuOpen}
                onAuthorMenuClick={handleAuthorMenuClick}
                toggleMenu={handleSubmenuItemClick}
              />
            </ListItem>
          )}
        </List>
      </Box>

      {/* 2. PINNED FOOTER (Stays cleanly at the bottom without overlapping content) */}
      <Box
        sx={{
          flexShrink: 0,
          p: 2,
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          bgcolor: '#FFF8EE',
        }}
      >
        <Typography variant="caption" display="block" color="text.secondary">
          Version {APP_VERSION} Build {BUILD_NUMBER}
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          Copyright © {APP_COPYRIGHT_YEARS} mobidawg llc. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Menu;

